import { useEffect, useRef, useState } from 'react';
import { cx } from '../components/ui';
import { REACTION_META } from '../lib/reactions';
import { usePrefersReducedMotion } from '../lib/useReducedMotion';
import type { Reaction } from '../lib/types';

/**
 * Reactions surfacing as the playhead reaches them.
 *
 * This is the thing that makes a recording feel watched rather than filed. As
 * the playhead crosses a moment somebody reacted to, that reaction appears
 * beside the picture for a beat and goes.
 *
 * With `prefers-reduced-motion`, they still appear: suppressing the
 * information because somebody gets motion sick would be the wrong reading of
 * that setting. They simply hold still and fade, with no drift and no scale.
 *
 * The layer is `aria-hidden`. It is a decoration on top of information that is
 * already stated plainly and permanently in the totals and on the marker rail,
 * and a live region firing every few seconds through a recording would be
 * unusable.
 */

/** How long a pop stays up. Slightly longer when it cannot move, to be read. */
const VISIBLE_MS = 1800;
const VISIBLE_MS_STILL = 2200;

/**
 * A jump larger than this is a seek, not playback. Firing every reaction the
 * viewer scrubbed past would be a confetti cannon.
 */
const MAX_STEP_SEC = 1.5;

interface Pop {
  key: string;
  reaction: Reaction;
}

let popSeq = 0;

export function ReactionPops({
  reactions,
  currentTime,
  className,
}: {
  reactions: readonly Reaction[];
  currentTime: number;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const [pops, setPops] = useState<Pop[]>([]);
  const lastTimeRef = useRef(currentTime);
  const reactionsRef = useRef(reactions);
  reactionsRef.current = reactions;
  const lifetimeRef = useRef(VISIBLE_MS);
  lifetimeRef.current = reduced ? VISIBLE_MS_STILL : VISIBLE_MS;
  // Each pop expires on its own timer. They are tracked so unmounting mid-play
  // does not leave a setState firing into a dead component.
  const timersRef = useRef<number[]>([]);

  useEffect(
    () => () => {
      for (const timer of timersRef.current) window.clearTimeout(timer);
      timersRef.current = [];
    },
    [],
  );

  useEffect(() => {
    const previous = lastTimeRef.current;
    lastTimeRef.current = currentTime;

    const step = currentTime - previous;
    if (step <= 0 || step > MAX_STEP_SEC) return;

    // The window is exclusive at the bottom so a reaction never fires twice.
    // From a standing start that would swallow anything pinned at 0:00, which
    // is a real timecode, so the very first step reaches below zero.
    const lower = previous === 0 ? -1 : previous;
    const crossed = reactionsRef.current.filter(
      (reaction) => reaction.timecodeSec > lower && reaction.timecodeSec <= currentTime,
    );
    if (crossed.length === 0) return;

    // Four is plenty. A moment with thirty reactions on it should read as
    // "lots", not fill the frame.
    const shown = crossed.slice(0, 4).map((reaction) => {
      popSeq += 1;
      return { key: `${reaction.id}:${popSeq}`, reaction };
    });

    setPops((current) => [...current, ...shown]);

    const keys = new Set(shown.map((pop) => pop.key));
    const timer = window.setTimeout(() => {
      timersRef.current = timersRef.current.filter((id) => id !== timer);
      setPops((current) => current.filter((pop) => !keys.has(pop.key)));
    }, lifetimeRef.current);
    timersRef.current.push(timer);
  }, [currentTime]);

  if (pops.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      className={cx('pointer-events-none flex flex-col-reverse items-end gap-1', className)}
    >
      {pops.map((pop) => {
        const meta = REACTION_META[pop.reaction.kind];
        return (
          <span
            key={pop.key}
            className={cx(
              'flex items-center gap-1.5 rounded-full border border-chalk/20 bg-slate-950/85 px-2 py-1',
              'text-caption text-chalk backdrop-blur-sm',
              reduced ? 'reaction-pop-still' : 'reaction-pop',
            )}
          >
            <span className="text-[15px] leading-none">{meta.glyph}</span>
            <span>{meta.label}</span>
          </span>
        );
      })}
    </div>
  );
}
